/**
 * TanStack Query keys centralizadas.
 *
 * - Factories com parâmetros dinâmicos devem reproduzir as tuplas já usadas no app
 *   ao migrar telas existentes (evita cache órfão).
 * - Use `*.all` / raízes hierárquicas para `invalidateQueries` em lote.
 *
 * @example
 * useQuery({ queryKey: queryKeys.checkin.staffDetails(staffToken), queryFn })
 * queryClient.invalidateQueries({ queryKey: queryKeys.checkin.staffDetails(staffToken) })
 */

export const queryKeys = {
  events: {
    all: ['events'] as const,
    lists: () => [...queryKeys.events.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.events.lists(), filters] as const,
    details: () => [...queryKeys.events.all, 'detail'] as const,
    detail: (eventId: string) => [...queryKeys.events.details(), eventId] as const,
    create: () => [...queryKeys.events.all, 'create'] as const,
    edit: (eventId: string) => [...queryKeys.events.all, 'edit', eventId] as const,
    jobsLotPending: (eventId: string) => [...queryKeys.events.all, 'jobsLotPending', eventId] as const,
    duplicate: (sourceEventId: string) => [...queryKeys.events.all, 'duplicate', sourceEventId] as const,
    categories: () => [...queryKeys.events.all, 'categories'] as const,
    saleStrategies: () => [...queryKeys.events.all, 'saleStrategies'] as const,
    eventSaleStrategies: (eventId: string) => [...queryKeys.events.saleStrategies(), eventId] as const,
    publicCategories: () => [...queryKeys.events.all, 'publicCategories'] as const
  },

  participants: {
    all: ['participants'] as const,
    details: () => [...queryKeys.participants.all, 'detail'] as const,
    detail: (eventId: string, cliCod: string) => [...queryKeys.participants.details(), eventId, cliCod] as const,
    lists: () => [...queryKeys.participants.all, 'list'] as const,
    list: (eventId: string) => [...queryKeys.participants.lists(), eventId] as const,
    tags: () => [...queryKeys.participants.all, 'tags'] as const,
    checkin: (eventId: string) => [...queryKeys.participants.all, 'checkin', eventId] as const
  },

  myEvents: {
    all: ['myEvents'] as const,
    allDetails: (eventId: string) => [...queryKeys.myEvents.all, 'allDetails', eventId] as const,
    customOffers: (eventId: string) => [...queryKeys.myEvents.all, 'customOffers', eventId] as const,
    eventPage: (eventId: string) => [...queryKeys.myEvents.all, 'eventPage', eventId] as const,
    eventPanel: (eventId: string) => [...queryKeys.myEvents.all, 'eventPanel', eventId] as const,
    eventPanelStatistics: (eventId: string) => [...queryKeys.myEvents.eventPanel(eventId), 'statistics'] as const,
    customTemplate: (eventId: string) => [...queryKeys.myEvents.all, 'customTemplate', eventId] as const,
    sponsors: (eventId: string) => [...queryKeys.myEvents.all, 'sponsors', eventId] as const,
    speakers: (eventId: string) => [...queryKeys.myEvents.all, 'speakers', eventId] as const,
    schedules: (eventId: string) => [...queryKeys.myEvents.all, 'schedules', eventId] as const
  },

  admin: {
    all: ['admin'] as const,
    myEventsList: () => [...queryKeys.admin.all, 'myEventsList'] as const,
    archiveEvent: (eventId: string) => [...queryKeys.admin.myEventsList(), 'archiveEvent', eventId] as const,
    event: (eventId: string) => [...queryKeys.admin.all, 'event', eventId] as const,
    eventEdit: (eventId: string) => [...queryKeys.admin.all, 'eventEdit', eventId] as const,
    eventIdByCntCod: (cntCod: string) => [...queryKeys.admin.all, 'eventIdByCntCod', cntCod] as const,
    attendanceList: (eventId: string) => [...queryKeys.admin.all, 'attendanceList', eventId] as const,
    attendanceListTags: () => [...queryKeys.admin.all, 'attendanceList', 'tags'] as const,
    attendanceListParticipants: (eventId: string, filter?: Record<string, unknown>) =>
      [...queryKeys.admin.attendanceList(eventId), 'participants', filter] as const,
    attendanceInviteTags: (inviteKey: string) =>
      [...queryKeys.admin.all, 'attendanceList', 'inviteTags', inviteKey] as const,
    attendanceImportStatus: (msqId: string) =>
      [...queryKeys.admin.all, 'attendanceList', 'importStatus', msqId] as const,
    rescheduleFutureEvents: (eventId: string) =>
      [...queryKeys.admin.attendanceList(eventId), 'rescheduleFutureEvents'] as const,
    rescheduleTickets: (eventId: string) => [...queryKeys.admin.attendanceList(eventId), 'rescheduleTickets'] as const,
    helpCenterFaq: () => [...queryKeys.admin.all, 'helpCenter', 'faq'] as const
  },

  layout: {
    all: ['layout'] as const,
    privateLayout: () => [...queryKeys.layout.all, 'privateLayout'] as const,
    loginAsEmail: () => [...queryKeys.layout.privateLayout(), 'loginAsEmail'] as const,
    acpoAccounts: () => [...queryKeys.layout.privateLayout(), 'acpoAccounts'] as const
  },

  shared: {
    all: ['shared'] as const,
    eventList: (filters?: Record<string, unknown>) => [...queryKeys.shared.all, 'eventList', filters] as const,
    eventListLast: (filters?: Record<string, unknown>) =>
      [...queryKeys.shared.all, 'eventList', 'last', filters] as const,
    eventListPast: (filters?: Record<string, unknown>) =>
      [...queryKeys.shared.all, 'eventList', 'past', filters] as const,
    eventListCurrent: (filters?: Record<string, unknown>) =>
      [...queryKeys.shared.all, 'eventList', 'current', filters] as const,
    resendTicket: () => [...queryKeys.shared.all, 'resendTicket'] as const,
    resendTicketSingle: (eventId: string) => [...queryKeys.shared.resendTicket(), 'single', eventId] as const,
    resendTicketMultiple: (eventId: string) => [...queryKeys.shared.resendTicket(), 'multiple', eventId] as const
  },

  myTickets: {
    all: ['myTickets'] as const,
    lists: () => [...queryKeys.myTickets.all, 'eventList'] as const,
    listCurrent: (filters?: Record<string, unknown>) => [...queryKeys.myTickets.lists(), 'current', filters] as const,
    listPast: (filters?: Record<string, unknown>) => [...queryKeys.myTickets.lists(), 'past', filters] as const,
    detail: (eventId: string) => [...queryKeys.myTickets.all, 'detail', eventId] as const,
    tickets: (eventId: string) => [...queryKeys.myTickets.all, 'detail', eventId, 'tickets'] as const,
    participantTickets: (eventId: string, attendanceId: string) =>
      [...queryKeys.myTickets.all, 'detail', eventId, 'participant', attendanceId] as const,
    transferTicket: (eventId: string) => [...queryKeys.myTickets.all, 'mutation', 'transferTicket', eventId] as const,
    setAsMine: (eventId: string) => [...queryKeys.myTickets.all, 'mutation', 'setAsMine', eventId] as const,
    unlinkTicket: (eventId: string) => [...queryKeys.myTickets.all, 'mutation', 'unlinkTicket', eventId] as const,
    createTransferLink: (eventId: string) =>
      [...queryKeys.myTickets.all, 'mutation', 'createTransferLink', eventId] as const,
    getTransferLink: (eventId: string) => [...queryKeys.myTickets.all, 'mutation', 'getTransferLink', eventId] as const,
    cancelTransferLink: (eventId: string) =>
      [...queryKeys.myTickets.all, 'mutation', 'cancelTransferLink', eventId] as const,
    sevenSummitUserByEmail: ['myTickets', 'mutation', 'sevenSummitUserByEmail'] as const,
    sevenSummitRepresent: (eventId: string) =>
      [...queryKeys.myTickets.all, 'mutation', 'sevenSummitRepresent', eventId] as const
  },

  checkin: {
    all: ['checkin'] as const,
    staffAuth: (token: string) => ['staffAuth', token] as const,
    staffDetails: (staffToken: string) => ['staffDetails', staffToken] as const,
    staffTags: (staffToken: string) => ['staffTags', staffToken] as const,
    checkinHistory: (staffToken: string) => ['checkinHistory', staffToken] as const,
    staffList: (eventId: string) => ['staffList', eventId] as const,
    staffByUserId: () => ['staffByUserId'] as const,
    eventCheckins: () => ['eventCheckins'] as const
  },

  attendanceList: {
    all: ['attendanceList'] as const,
    assignedTags: <TSelected>(selectedParticipants: TSelected, eventId: string) =>
      ['attendanceListAssignedTags', selectedParticipants, eventId] as const
  },

  eventShelf: {
    all: ['eventShelf'] as const,
    products: (eventId: string) => ['eventShelfProducts', eventId] as const,
    settings: (eventId: string) => ['eventShelfSettings', eventId] as const,
    myEduzzProducts: () => ['myEduzzProducts'] as const
  },

  publicPages: {
    all: ['public'] as const,
    shelf: (eventId: string) => ['shelfData', eventId] as const,
    eventReview: (eventId: string) => ['eventReview', eventId] as const,
    eventReviewCollector: (eventId: string) => ['event-review-collector', eventId] as const,
    giftTicketToken: (token: string) => ['giftTicketToken', token] as const,
    claimGiftTicket: (token: string) => ['claimGiftTicket', token] as const,
    preCheckoutDetails: (eventId: string, transactionKey: string) =>
      ['preCheckoutDetails', eventId, transactionKey] as const
  },

  auth: {
    all: ['auth'] as const,
    session: () => [...queryKeys.auth.all, 'session'] as const,
    user: () => [...queryKeys.auth.all, 'authenticatedUser'] as const,
    userDetails: () => [...queryKeys.auth.all, 'userDetails'] as const,
    token: () => [...queryKeys.auth.all, 'token'] as const,
    routes: () => [...queryKeys.auth.all, 'routes'] as const
  },

  participant: {
    all: ['participant'] as const,
    tickets: (ciphertext: string, utmSource?: string | null) =>
      [...queryKeys.participant.all, 'tickets', ciphertext, utmSource ?? null] as const
  }
} as const;

export type QueryKeys = typeof queryKeys;
